import React, { useRef } from 'react';
import styled from 'styled-components';
import { motion, useInView } from 'framer-motion';
import { FaBriefcase, FaCalendarAlt, FaMapMarkerAlt } from 'react-icons/fa';

const Experience = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, threshold: 0.1 });

  // Work experiences from resume
  const experiences = [
    {
      title: "Data Engineer",
      company: "Fusemachines Nepal",
      period: "Aug 2022 - Present",
      location: "Kathmandu, Nepal",
      description: [
        "Designed and implemented end-to-end ELT pipelines using Spark SQL, Python, Airbyte, AWS Glue, Apache Airflow, and Notebooks to facilitate seamless data integration and real-time analytics.",
        "Optimized ELT processes by enhancing data transformations and leveraging parallel processing techniques, reducing overall processing time by up to 30%.",
        "Developed and maintained interactive dashboards and visualizations using Apache Superset, translating complex data sets into actionable insights for cross-functional teams.",
        "Led troubleshooting, testing, deployment, and production support efforts to ensure high availability, data security, and regulatory compliance.",
        "Mentored junior engineers and conducted training sessions on Apache Spark and Apache Airflow, fostering best practices in data pipeline development and optimization.",
        "Delivered demos on data ingestion architecture, real-time data processing and alerting mechanisms, driving client adoption of Fabric solutions.",
        "Collaborated with senior engineers as a co-interviewer for mid-senior level Azure and AWS engineering roles, contributing to the recruitment process by assessing technical expertise and cultural fit."
      ]
    }
  ];

  const projects = [
    {
      title: "NYCCFB",
      company: "Azure (SQL Server, ADF, Synapse), Fabric (Pipelines, Eventstream, Notebooks)",
      period: "Jan 2023 - Present",
      description: [
        "Modernized data Ingestion and Transformation processes by replacing legacy technologies with Microsoft Fabric, leveraging Fabric Pipelines Warehouse and Lakehouse for scalable and efficient data management.",
        "Conducted research and resolved issues related to Microsoft Fabric, collaborating with Microsoft support for timely resolutions.",
        "Researched and applied Delta tables in Fabric Lakehouse for efficient data storage and query optimization, improving data processing performance by 25%.",
        "Contributed to the development of a Data Quality Framework using Fabric Pipelines and PySpark, a one-stop solution for all data profiling needs, and successfully deployed it to production.",
        "Implemented real-time data processing using SQL CDC (Change Data Capture) in Azure SQL Server, Fabric Eventstream, and Spark Streaming for seamless data integration and real-time analytics.",
        "Successfully deployed a streaming application to production using a CI/CD approach with Fabric's deployment pipelines, ensuring reliability and scalability."
      ]
    },
    {
      title: "Internal Analytics",
      company: "PySpark, AWS (EC2, Glue, Athena), Airbyte, Airflow, Superset",
      period: "Jan 2023 - Dec 2023",
      description: [
        "Conducted tool research and resolved issues related to data ingestion and transformation pipelines.",
        "Loaded and transformed data from multiple sources as per business requirements.",
        "Created business logic-based views and visualizations using Superset for actionable insights.",
        "Hosted and integrated cloud tools (Airflow, Airbyte, Superset) to build a scalable data infrastructure.",
        "Deployed the development environment to production and troubleshooted issues faced during the transition.",
        "Reduced data processing time by 30% by optimizing PySpark jobs and Airflow Dags"
      ]
    }
  ];

  const fadeInVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <ExperienceSection id="experience" ref={ref}>
      <div className="container">
        <SectionHeader
          variants={fadeInVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          transition={{ duration: 0.6 }}
        >
          <h2>Experience</h2>
          <Divider />
        </SectionHeader>

        <ExperienceContainer>
          <ExperienceTitle
            variants={fadeInVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Work Experience
          </ExperienceTitle>

          <Timeline>
            {experiences.map((exp, index) => (
              <TimelineItem
                key={index}
                variants={fadeInVariants}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
                transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
              >
                <TimelineIconContainer>
                  <TimelineIcon>
                    <FaBriefcase />
                  </TimelineIcon>
                </TimelineIconContainer>
                <TimelineContent>
                  <ExperienceHeader>
                    <h3>{exp.title}</h3>
                    <Company>{exp.company}</Company>
                  </ExperienceHeader>
                  <ExperienceMeta>
                    <MetaItem>
                      <FaCalendarAlt />
                      <span>{exp.period}</span>
                    </MetaItem>
                    <MetaItem>
                      <FaMapMarkerAlt />
                      <span>{exp.location}</span>
                    </MetaItem>
                  </ExperienceMeta>
                  <Responsibilities>
                    {exp.description.map((item, idx) => (
                      <ResponsibilityItem key={idx}>
                        {item}
                      </ResponsibilityItem>
                    ))}
                  </Responsibilities>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>

          <ExperienceTitle
            variants={fadeInVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            transition={{ duration: 0.6, delay: 0.2 + experiences.length * 0.1 }}
          >
            Projects
          </ExperienceTitle>

          <Timeline>
            {projects.map((project, index) => (
              <TimelineItem
                key={index}
                variants={fadeInVariants}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
                transition={{ duration: 0.6, delay: 0.3 + (experiences.length + index) * 0.1 }}
              >
                <TimelineIconContainer>
                  <TimelineIcon project>
                    <FaBriefcase />
                  </TimelineIcon>
                </TimelineIconContainer>
                <TimelineContent>
                  <ExperienceHeader>
                    <h3>{project.title}</h3>
                    <Company>{project.company}</Company>
                  </ExperienceHeader>
                  <ExperienceMeta>
                    <MetaItem>
                      <FaCalendarAlt />
                      <span>{project.period}</span>
                    </MetaItem>
                  </ExperienceMeta>
                  <Responsibilities>
                    {project.description.map((item, idx) => (
                      <ResponsibilityItem key={idx}>
                        {item}
                      </ResponsibilityItem>
                    ))}
                  </Responsibilities>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        </ExperienceContainer>
      </div>
    </ExperienceSection>
  );
};

// Styled Components
const ExperienceSection = styled.section`
    padding: 100px 0;
    background-color: var(--primary);

    .container {
        max-width: 1200px;
        margin: 0 auto;
        padding: 0 2rem;
    }
`;

const SectionHeader = styled(motion.div)`
    margin-bottom: 60px;
    text-align: center;

    h2 {
        font-size: 2.5rem;
        color: var(--text-light);
        margin-bottom: 1rem;
        position: relative;
        display: inline-block;
    }
`;

const Divider = styled.div`
    height: 4px;
    width: 70px;
    background-color: var(--accent);
    margin: 0 auto;
`;

const ExperienceContainer = styled.div`
    max-width: 900px;
    margin: 0 auto;
`;

const ExperienceTitle = styled(motion.h3)`
    color: var(--text-light);
    font-size: 1.8rem;
    margin-bottom: 30px;
    padding-bottom: 10px;
    border-bottom: 1px dashed rgba(30, 136, 229, 0.3);
`;

const Timeline = styled.div`
    position: relative;
    margin-bottom: 60px;

    &::before {
        content: '';
        position: absolute;
        left: 20px;
        top: 0;
        height: 100%;
        width: 2px;
        background-color: rgba(30, 136, 229, 0.3);

        @media (max-width: 768px) {
            left: 15px;
        }
    }
`;

const TimelineItem = styled(motion.div)`
    padding-left: 60px;
    position: relative;
    margin-bottom: 40px;

    @media (max-width: 768px) {
        padding-left: 45px;
    }

    &:last-child {
        margin-bottom: 0;
    }
`;

const TimelineIconContainer = styled.div`
    position: absolute;
    left: 0;
    top: 0;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;

    @media (max-width: 768px) {
        width: 30px;
        height: 30px;
    }
`;

const TimelineIcon = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: ${props => props.project ? "rgba(30, 136, 229, 0.2)" : "rgba(30, 136, 229, 0.2)"};
    border: 2px solid ${props => props.project ? "var(--accent)" : "var(--accent)"};
    color: ${props => props.project ? "var(--accent)" : "var(--accent)"};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;

    @media (max-width: 768px) {
        width: 30px;
        height: 30px;
        font-size: 0.9rem;
    }
`;

const TimelineContent = styled.div`
    background-color: var(--secondary);
    padding: 25px;
    border-radius: 6px;
    box-shadow: 0 10px 30px -15px var(--shadow);
    transition: all 0.3s ease;

    &:hover {
        transform: translateY(-5px);
        box-shadow: 0 20px 30px -15px var(--shadow);
    }
`;

const ExperienceHeader = styled.div`
    margin-bottom: 10px;

    h3 {
        color: var(--text-light);
        font-size: 1.4rem;
        margin-bottom: 5px;
    }
`;

const Company = styled.div`
    color: var(--accent);
    font-size: 1rem;
    font-weight: 500;
`;

const ExperienceMeta = styled.div`
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 15px;
    gap: 15px;
`;

const MetaItem = styled.div`
    display: flex;
    align-items: center;
    color: var(--text-secondary);
    font-size: 0.9rem;

    svg {
        margin-right: 8px;
        color: var(--accent);
    }
`;

const Responsibilities = styled.ul`
    padding-left: 20px;
`;

const ResponsibilityItem = styled.li`
    color: var(--text-secondary);
    font-size: 1rem;
    line-height: 1.6;
    margin-bottom: 8px;
    position: relative;

    &::marker {
        color: var(--accent);
    }

    &:last-child {
        margin-bottom: 0;
    }
`;

export default Experience;
